import React, { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Loader2, Phone, Send, X } from "lucide-react";
import {
  followUpAddCommentApi,
  followUpCommentsApi,
  followUpCustomerItemsApi,
} from "../../redux/apis/follow-up-api";
import {
  FOLLOW_UP_COMMENT_MAX_LENGTH,
  formatDateTime,
  formatDisplayDate,
  formatQty,
  getCustomerName,
  getDueState,
  getInitials,
  hasCallablePhone,
} from "../../utils/follow-up";
import errorToast from "../helper-components/error-toast";
import BadgesComponent from "../tables/badges-component";
import { DueBadge } from "./follow-up-badges";
import { CopyPhoneButton, StatusToggleButton, WhatsAppButton } from "./follow-up-actions";

const apiErrorMessage = (error, fallback) =>
  error?.response?.data?.message ?? fallback;

const SectionHeading = ({ id, children, count }) => (
  <h3
    id={id}
    className="flex items-center gap-2 text-[length:var(--follow-up-small-fs)] font-[var(--follow-up-label-fw)] uppercase tracking-wide text-[var(--follow-up-muted-fg)]"
  >
    {children}
    {count !== undefined && (
      <span className="rounded-full bg-[var(--follow-up-count-bg)] px-2 text-[var(--follow-up-text-fg)]">
        {count}
      </span>
    )}
  </h3>
);

const Muted = ({ children }) => (
  <p className="py-3 text-[length:var(--follow-up-small-fs)] text-[var(--follow-up-muted-fg)]">
    {children}
  </p>
);

// The customer's pending items: what to go through on the call
const useCustomerItems = (rfuId, phone) => {
  const [state, setState] = useState({ items: [], isLoading: false, error: "" });
  useEffect(() => {
    if (!rfuId || !hasCallablePhone(phone)) {
      setState({ items: [], isLoading: false, error: "" });
      return undefined;
    }
    let cancelled = false;
    setState({ items: [], isLoading: true, error: "" });
    followUpCustomerItemsApi({ phone })
      .then((res) => {
        if (!cancelled) setState({ items: res?.data?.data ?? [], isLoading: false, error: "" });
      })
      .catch((error) => {
        if (!cancelled) {
          setState({
            items: [],
            isLoading: false,
            error: apiErrorMessage(error, "Couldn't load the customer's items"),
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [rfuId, phone]);
  return state;
};

const useComments = (rfuId) => {
  const [state, setState] = useState({ comments: [], isLoading: false, error: "" });
  useEffect(() => {
    if (!rfuId) return undefined;
    let cancelled = false;
    setState({ comments: [], isLoading: true, error: "" });
    followUpCommentsApi(rfuId)
      .then((res) => {
        if (!cancelled) setState({ comments: res?.data?.data ?? [], isLoading: false, error: "" });
      })
      .catch((error) => {
        if (!cancelled) {
          setState({
            comments: [],
            isLoading: false,
            error: apiErrorMessage(error, "Couldn't load the comments"),
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [rfuId]);
  const prependComment = (comment) =>
    setState((previous) => ({ ...previous, comments: [comment, ...previous.comments] }));
  return { ...state, prependComment };
};

// The items listed for the customer: their pending refills, or just this
// follow-up's item when there is no phone to look the others up by
const getShownItems = (followUp, items) =>
  hasCallablePhone(followUp?.cmr_phone_number) ? items : [followUp];

// The customer's items, each with a tick for the WhatsApp message
const ItemsSection = ({ shownItems, isLoading, error, selectedIds, onSelectionChange }) => {
  const allIds = shownItems.map((item) => item.rfu_id);
  const isAllSelected = allIds.length > 0 && allIds.every((id) => selectedIds.includes(id));
  const toggleItem = (rfuId) =>
    onSelectionChange(
      selectedIds.includes(rfuId)
        ? selectedIds.filter((id) => id !== rfuId)
        : [...selectedIds, rfuId]
    );

  return (
    <section aria-labelledby="follow-up-items-heading" className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-2">
        <SectionHeading id="follow-up-items-heading" count={isLoading ? undefined : shownItems.length}>
          Pending refills for this customer
        </SectionHeading>
        {shownItems.length > 1 && (
          <button
            type="button"
            onClick={() => onSelectionChange(isAllSelected ? [] : allIds)}
            className="text-[length:var(--follow-up-small-fs)] font-[var(--follow-up-label-fw)] text-[var(--follow-up-accent)] hover:underline"
          >
            {isAllSelected ? "Clear all" : "Select all"}
          </button>
        )}
      </div>
      {shownItems.length > 0 && (
        <p className="text-[length:var(--follow-up-small-fs)] text-[var(--follow-up-muted-fg)]">
          Tick the items to send in the WhatsApp message.
        </p>
      )}
      {isLoading && <Muted>Loading items…</Muted>}
      {error && <Muted>{error}</Muted>}
      {!isLoading && !error && shownItems.length === 0 && (
        <Muted>No pending refills for this customer.</Muted>
      )}
      <ul className="flex flex-col gap-1">
        {shownItems.map((item) => {
          const shortage = parseFloat(item.item_shortage_qty) || 0;
          const isSelected = selectedIds.includes(item.rfu_id);
          return (
            <li key={item.rfu_id}>
              <label
                className={`flex cursor-pointer items-start gap-3 rounded-lg px-2 py-3 transition-colors ${
                  isSelected
                    ? "bg-[var(--follow-up-accent-soft-bg)]"
                    : "hover:bg-[var(--list-table-row-hover-bg)]"
                }`}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleItem(item.rfu_id)}
                  className="custom-checkbox mt-0.5 shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[length:var(--follow-up-text-fs)] font-[var(--follow-up-label-fw)] text-[var(--follow-up-title-fg)]">
                    {item.item_name}
                  </p>
                  {/* the shortage is for the store's reference; the message doesn't include it */}
                  <p className="text-[length:var(--follow-up-small-fs)] text-[var(--follow-up-muted-fg)]">
                    Qty {formatQty(item.item_quantity)}
                    {shortage > 0 && (
                      <span className="text-[var(--follow-up-overdue-fg)]">
                        {" "}· Shortage {formatQty(shortage)}
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <span className="text-[length:var(--follow-up-small-fs)] text-[var(--follow-up-text-fg)]">
                    {formatDisplayDate(item.item_refill_date)}
                  </span>
                  <DueBadge dueState={getDueState(item.item_refill_date, "pending")} />
                </div>
              </label>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

const CommentsSection = ({ comments, isLoading, error }) => (
  <section aria-labelledby="follow-up-comments-heading" className="flex flex-col gap-2">
    <SectionHeading id="follow-up-comments-heading" count={isLoading ? undefined : comments.length}>
      Comments
    </SectionHeading>
    {isLoading && <Muted>Loading comments…</Muted>}
    {error && <Muted>{error}</Muted>}
    {!isLoading && !error && comments.length === 0 && (
      <Muted>No comments yet. Note what the customer said on the call.</Muted>
    )}
    <ol className="flex flex-col gap-2">
      {comments.map((comment) => (
        <li
          key={comment.rfc_id}
          className="rounded-lg border border-[var(--follow-up-comment-border)] bg-[var(--follow-up-comment-bg)] p-3"
        >
          <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
            <span className="text-[length:var(--follow-up-small-fs)] font-[var(--follow-up-label-fw)] text-[var(--follow-up-comment-author-fg)]">
              {comment.created_user_name || "Staff"}
            </span>
            <time
              dateTime={comment.created_date}
              className="text-[length:var(--follow-up-small-fs)] text-[var(--follow-up-muted-fg)]"
            >
              {formatDateTime(comment.created_date)}
            </time>
          </div>
          <p className="whitespace-pre-wrap break-words text-[length:var(--follow-up-text-fs)] text-[var(--follow-up-text-fg)]">
            {comment.rfc_comment}
          </p>
        </li>
      ))}
    </ol>
  </section>
);

const CommentComposer = ({ rfuId, onAdded }) => {
  const [draft, setDraft] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => setDraft(""), [rfuId]);

  const addComment = async () => {
    const comment = draft.trim();
    if (!comment || isSaving) return;
    setIsSaving(true);
    try {
      const res = await followUpAddCommentApi({ rfu_id: rfuId, comment });
      onAdded(res?.data?.data);
      setDraft("");
    } catch (error) {
      errorToast(apiErrorMessage(error, "Couldn't add the comment"));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form
      className="flex flex-col gap-2 border-t border-[var(--follow-up-border)] p-4"
      onSubmit={(event) => {
        event.preventDefault();
        addComment();
      }}
    >
      <label
        htmlFor="follow-up-comment"
        className="text-[length:var(--follow-up-small-fs)] font-[var(--follow-up-label-fw)] text-[var(--follow-up-text-fg)]"
      >
        Add a comment
      </label>
      <textarea
        id="follow-up-comment"
        rows={3}
        value={draft}
        maxLength={FOLLOW_UP_COMMENT_MAX_LENGTH}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
            event.preventDefault();
            addComment();
          }
        }}
        placeholder="e.g. Customer will pick up on Friday"
        className="w-full resize-none rounded-lg border border-[var(--follow-up-chip-border)] bg-[var(--follow-up-surface-bg)] p-3 text-[length:var(--follow-up-text-fs)] text-[var(--follow-up-text-fg)] placeholder:text-[var(--follow-up-muted-fg)] focus:outline-none focus:border-[var(--follow-up-accent)]"
      />
      <div className="flex items-center justify-between gap-3">
        <span className="text-[length:var(--follow-up-small-fs)] text-[var(--follow-up-muted-fg)]">
          {draft.length}/{FOLLOW_UP_COMMENT_MAX_LENGTH} · Ctrl + Enter to add
        </span>
        <button
          type="submit"
          disabled={!draft.trim() || isSaving}
          className="inline-flex h-9 items-center gap-2 rounded-lg bg-[var(--follow-up-accent)] px-4 text-[length:var(--follow-up-text-fs)] font-[var(--follow-up-label-fw)] text-[var(--follow-up-accent-fg)] transition-colors hover:bg-[var(--follow-up-accent-hover)] disabled:cursor-not-allowed disabled:bg-[var(--follow-up-disabled-bg)] disabled:text-[var(--follow-up-disabled-fg)]"
        >
          {isSaving ? (
            <Loader2 size={16} className="animate-spin" aria-hidden="true" />
          ) : (
            <Send size={16} aria-hidden="true" />
          )}
          Add comment
        </button>
      </div>
    </form>
  );
};

// Slide-over with everything for one follow-up call: who to call, the
// customer's pending items and the comments
const FollowUpDetailsPanel = ({
  followUp,
  busyAction,
  onClose,
  onCommentAdded,
  onWhatsApp,
  onToggleStatus,
}) => {
  // keeps showing the last follow-up while the panel slides out
  const [shown, setShown] = useState(followUp);
  useEffect(() => {
    if (followUp) setShown(followUp);
  }, [followUp]);

  const rfuId = followUp?.rfu_id;
  const items = useCustomerItems(rfuId, followUp?.cmr_phone_number);
  const comments = useComments(rfuId);
  const customerName = getCustomerName(shown);
  const phone = shown?.cmr_phone_number;

  // the items ticked for the WhatsApp message: the opened one to start with
  const [selectedIds, setSelectedIds] = useState([]);
  useEffect(() => {
    setSelectedIds(rfuId ? [rfuId] : []);
  }, [rfuId]);
  const shownItems = shown ? getShownItems(shown, items.items) : [];
  const selectedItems = shownItems.filter((item) => selectedIds.includes(item.rfu_id));

  return (
    <Transition.Root show={!!followUp} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0" style={{ background: "var(--follow-up-overlay-bg)" }} />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full sm:pl-10">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-out duration-200"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in duration-150"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel
                className="pointer-events-auto flex h-full w-screen max-w-lg flex-col bg-[var(--follow-up-surface-bg)]"
                style={{ boxShadow: "var(--follow-up-shadow-panel)" }}
              >
                {shown && (
                  <>
                    <header className="flex flex-col gap-4 border-b border-[var(--slide-panel-border)] bg-[var(--slide-panel-head-bg)] p-4 sm:p-5">
                      <div className="flex items-start gap-3">
                        <span
                          aria-hidden="true"
                          className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-[var(--follow-up-navy)] text-[length:var(--follow-up-text-fs)] font-[var(--follow-up-title-fw)] text-[var(--follow-up-accent-fg)]"
                        >
                          {getInitials(customerName)}
                        </span>
                        <div className="min-w-0 flex-1">
                          <Dialog.Title className="truncate text-[length:var(--slide-panel-h-fs)] font-[var(--slide-panel-h-fw)] text-[var(--slide-panel-h-fg)]">
                            {customerName}
                          </Dialog.Title>
                          {phone ? (
                            <div className="flex items-center gap-1">
                              <a
                                href={`tel:${phone}`}
                                className="inline-flex items-center gap-1 text-[length:var(--follow-up-text-fs)] font-[var(--follow-up-label-fw)] text-[var(--follow-up-call-fg)] hover:underline"
                              >
                                <Phone size={14} aria-hidden="true" />
                                {phone}
                              </a>
                              <CopyPhoneButton phone={phone} />
                            </div>
                          ) : (
                            <p className="text-[length:var(--follow-up-small-fs)] text-[var(--follow-up-muted-fg)]">
                              No phone number on this bill
                            </p>
                          )}
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-[length:var(--follow-up-small-fs)] text-[var(--follow-up-muted-fg)]">
                            <BadgesComponent
                              status={shown.rfu_status === "done" ? "completed" : "pending"}
                            />
                            {shown.sot_invoice_number && <span>Bill {shown.sot_invoice_number}</span>}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={onClose}
                          aria-label="Close"
                          className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-[var(--follow-up-muted-fg)] transition-colors hover:bg-[var(--follow-up-chip-hover-bg)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--follow-up-focus-ring)]"
                        >
                          <X size={18} aria-hidden="true" />
                        </button>
                      </div>
                      <div className="flex gap-2">
                        <WhatsAppButton
                          followUp={shown}
                          itemCount={items.isLoading ? 0 : selectedItems.length}
                          onClick={() => onWhatsApp(shown, selectedItems)}
                          className="flex-1"
                        />
                        <StatusToggleButton
                          followUp={shown}
                          isBusy={busyAction === "status"}
                          onClick={onToggleStatus}
                          className="flex-1"
                        />
                      </div>
                    </header>

                    <div className="flex flex-1 flex-col gap-6 overflow-y-auto p-4 sm:p-5">
                      <ItemsSection
                        shownItems={shownItems}
                        isLoading={items.isLoading}
                        error={items.error}
                        selectedIds={selectedIds}
                        onSelectionChange={setSelectedIds}
                      />
                      <CommentsSection {...comments} />
                    </div>

                    <CommentComposer
                      rfuId={shown.rfu_id}
                      onAdded={(comment) => {
                        comments.prependComment(comment);
                        onCommentAdded(shown.rfu_id, comment);
                      }}
                    />
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default FollowUpDetailsPanel;
